import 'ag-grid-community/styles/ag-grid.css'; // Mandatory CSS required by the grid
import 'ag-grid-community/styles/ag-theme-quartz.css'; // Optional Theme applied to the grid
import './Worksheet.css';

import { Bars3Icon } from '@heroicons/react/24/outline';
import { ColDef, GridApi, IRowNode } from 'ag-grid-community';
import { useEffect, useRef, useState } from 'react';

import AuditGrid from '../components/AuditGrid';
import AuditSelect from '../components/AuditSelect';
import ExportXLSX from '../components/ExportXLSX';
import ImportCSV from '../components/ImportCSV';
import MicroWrapper from '../components/MicroWrapper';
import PasteGrid from '../components/PasteGrid';
import SlidingSidebar from '../components/SlidingSidebar';
import WorksheetProperties from '../components/WorksheetProperties';
import { selectedAudit } from '../state/audit';

function Worksheet() {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [rowCount, setRowCount] = useState(0);
    const [acceptedCount, setAcceptedCount] = useState(0);
    const [activeTab, setActiveTab] = useState<'grid' | 'properties'>('grid');
    const auditGridRef = useRef<{ api: GridApi } | null>(null);

    // Function to update row count and accepted count from the grid
    const updateRowCount = () => {
        if (auditGridRef.current?.api) {
            // Count all rows and accepted cards
            let totalCount = 0;
            let accepted = 0;

            auditGridRef.current.api.forEachNode((node: IRowNode) => {
                totalCount++;
                if (node.data && node.data.status === 'Accepted') {
                    accepted++;
                }
            });

            setRowCount(totalCount);
            setAcceptedCount(accepted);
        }
    };

    // Reset row count when audit changes
    useEffect(() => {
        setRowCount(0);
        setAcceptedCount(0);
    }, [selectedAudit.value?.id]);

    const handlePastedDataChange = (
        pastedData: Record<string, unknown>[],
        columnMapping: Record<string, string>,
    ) => {
        console.log('Processing pasted data:', pastedData);
        console.log('Column mapping:', columnMapping);

        // Check if we have the required columns
        if (pastedData.length === 0) return;

        // Find the Control IDs, Notes, and Result columns in pasted data
        let controlIdColumn: string | null = null;
        let notesColumn: string | null = null;
        let resultColumn: string | null = null;

        // Look for columns by their header names
        Object.entries(columnMapping).forEach(([field, header]) => {
            const headerLower = header.toLowerCase();
            if (headerLower === 'control ids' || headerLower === 'control id') {
                controlIdColumn = field;
            } else if (headerLower === 'notes' || headerLower === 'note') {
                notesColumn = field;
            } else if (headerLower === 'result' || headerLower === 'results') {
                resultColumn = field;
            }
        });

        if (!controlIdColumn || !notesColumn) {
            console.error(
                'Could not find required columns. Need "Control IDs" and "Notes" columns.',
            );
            alert('Please ensure your data has "Control IDs" and "Notes" columns.');
            return;
        }

        matchAndUpdateAuditGrid(pastedData, controlIdColumn, notesColumn, resultColumn);
    };

    const matchAndUpdateAuditGrid = async (
        pastedData: Record<string, unknown>[],
        controlIdColumn: string,
        notesColumn: string,
        resultColumn: string | null,
    ) => {
        if (!auditGridRef.current) {
            console.error('AuditGrid ref not available');
            return;
        }

        const gridApi = auditGridRef.current.api;
        if (!gridApi) {
            console.error('Grid API not available');
            return;
        }

        // Get all rows from AuditGrid
        const auditRows: Record<string, unknown>[] = [];
        gridApi.forEachNode((node: IRowNode) => {
            auditRows.push(node.data);
        });

        // Check if "Lexi Output" column exists in AuditGrid
        const columnDefs = gridApi.getColumnDefs();
        const lexiOutputExists = columnDefs?.some(
            (col: ColDef) => col.headerName === 'Lexi Output',
        );

        if (!lexiOutputExists) {
            // Add Lexi Output column
            const newColumnDefs = [
                ...(columnDefs || []),
                {
                    headerName: 'Lexi Output',
                    field: 'lexiOutput',
                    width: 200,
                    editable: true,
                    cellEditor: 'agLargeTextCellEditor',
                    cellEditorPopup: true,
                    wrapText: true,
                    autoHeight: true,
                },
            ];
            gridApi.setColumnDefs(newColumnDefs);
        }

        // Match rows and update Lexi Output and Lexi Conclusion
        let matchedCount = 0;
        let updatedOutputCount = 0;
        let updatedConclusionCount = 0;

        pastedData.forEach((pastedRow) => {
            const controlId = pastedRow[controlIdColumn];
            const notes = pastedRow[notesColumn];
            const result = resultColumn ? pastedRow[resultColumn] : null;

            if (!controlId) return;

            // Find all matching rows in AuditGrid by refId
            const matchingAuditRows = auditRows.filter(
                (auditRow) => String(auditRow.refId) === controlId.toString().trim(),
            );

            // Update all matching rows
            matchingAuditRows.forEach((matchingAuditRow) => {
                // Find the row node
                const rowNode = gridApi.getRowNode(String(matchingAuditRow.id));
                if (rowNode) {
                    let rowUpdated = false;

                    // Update Lexi Output if notes are provided
                    if (notes) {
                        const lexiOutputColumn = gridApi.getColumn('lexiOutput');
                        if (lexiOutputColumn) {
                            // Get the old value
                            const oldValue = matchingAuditRow.lexiOutput || '';

                            // Update the data in the row node
                            rowNode.setDataValue('lexiOutput', notes);
                            updatedOutputCount++;
                            rowUpdated = true;

                            // Create a simplified CellValueChangedEvent
                            const cellValueChangedEvent = {
                                data: rowNode.data,
                                column: lexiOutputColumn,
                                oldValue: oldValue,
                                newValue: notes,
                                node: rowNode,
                                api: gridApi,
                            };

                            // Try to dispatch the event using ag-Grid's event system
                            try {
                                // Use ag-Grid's internal event dispatch if available
                                if (gridApi.dispatchEvent) {
                                    gridApi.dispatchEvent({
                                        type: 'cellValueChanged',
                                        ...cellValueChangedEvent,
                                    });
                                } else {
                                    // Manual trigger by simulating the grid's internal behavior
                                    // This will be caught by the onCellValueChanged prop in AuditGrid
                                    // Event variable removed as it was unused

                                    // Try to find and call the onCellValueChanged callback
                                    // Check if we can access it through the grid's internals
                                    if (
                                        (
                                            gridApi as GridApi & {
                                                gridOptionsService?: {
                                                    gridOptions?: {
                                                        onCellValueChanged?: (
                                                            event: unknown,
                                                        ) => void;
                                                    };
                                                };
                                            }
                                        ).gridOptionsService?.gridOptions
                                            ?.onCellValueChanged
                                    ) {
                                        (
                                            gridApi as GridApi & {
                                                gridOptionsService?: {
                                                    gridOptions?: {
                                                        onCellValueChanged?: (
                                                            event: unknown,
                                                        ) => void;
                                                    };
                                                };
                                            }
                                        ).gridOptionsService?.gridOptions?.onCellValueChanged?.(
                                            cellValueChangedEvent,
                                        );
                                    }
                                }
                            } catch (error) {
                                console.warn(
                                    'Could not trigger onCellValueChanged event:',
                                    error,
                                );
                                // The data has still been updated via setDataValue above
                            }
                        }
                    }

                    // Update Lexi Conclusion if result is provided
                    if (result) {
                        const lexiConclusionColumn = gridApi.getColumn('lexiConclusion');
                        if (lexiConclusionColumn) {
                            // Get the old value
                            const oldConclusionValue =
                                matchingAuditRow.lexiConclusion || '';

                            // Update the data in the row node
                            rowNode.setDataValue('lexiConclusion', result);
                            updatedConclusionCount++;
                            rowUpdated = true;

                            // Create a simplified CellValueChangedEvent
                            const cellValueChangedEvent = {
                                data: rowNode.data,
                                column: lexiConclusionColumn,
                                oldValue: oldConclusionValue,
                                newValue: result,
                                node: rowNode,
                                api: gridApi,
                            };

                            // Try to dispatch the event using ag-Grid's event system
                            try {
                                // Use ag-Grid's internal event dispatch if available
                                if (gridApi.dispatchEvent) {
                                    gridApi.dispatchEvent({
                                        type: 'cellValueChanged',
                                        ...cellValueChangedEvent,
                                    });
                                } else {
                                    // Manual trigger by simulating the grid's internal behavior
                                    // This will be caught by the onCellValueChanged prop in AuditGrid
                                    // Event variable removed as it was unused

                                    // Try to find and call the onCellValueChanged callback
                                    // Check if we can access it through the grid's internals
                                    if (
                                        (
                                            gridApi as GridApi & {
                                                gridOptionsService?: {
                                                    gridOptions?: {
                                                        onCellValueChanged?: (
                                                            event: unknown,
                                                        ) => void;
                                                    };
                                                };
                                            }
                                        ).gridOptionsService?.gridOptions
                                            ?.onCellValueChanged
                                    ) {
                                        (
                                            gridApi as GridApi & {
                                                gridOptionsService?: {
                                                    gridOptions?: {
                                                        onCellValueChanged?: (
                                                            event: unknown,
                                                        ) => void;
                                                    };
                                                };
                                            }
                                        ).gridOptionsService?.gridOptions?.onCellValueChanged?.(
                                            cellValueChangedEvent,
                                        );
                                    }
                                }
                            } catch (error) {
                                console.warn(
                                    'Could not trigger onCellValueChanged event for conclusion:',
                                    error,
                                );
                                // The data has still been updated via setDataValue above
                            }
                        }
                    }

                    if (rowUpdated) {
                        matchedCount++;
                    }
                }
            });
        });

        console.log(
            `Matched ${matchedCount} rows. Updated ${updatedOutputCount} Lexi Outputs and ${updatedConclusionCount} Lexi Conclusions`,
        );
        alert(
            `Successfully updated ${matchedCount} rows:\n- ${updatedOutputCount} Lexi Outputs\n- ${updatedConclusionCount} Lexi Conclusions`,
        );
    };

    const headerContent = (
        <div className="w-full">
            <div className="px-4 py-5 flex w-full gap-4">
                <AuditSelect selectedAudit={selectedAudit}></AuditSelect>
                {selectedAudit && (
                    <>
                        <p className="p-2 text-sm text-gray-600 font-semibold">
                            Board ID: {selectedAudit.value?.id}
                        </p>
                        {activeTab === 'grid' && (
                            <>
                                <p className="p-2 text-sm text-gray-600 font-semibold">
                                    Rows Count: {rowCount}
                                </p>
                                <div className="p-2 flex items-center gap-2">
                                    <span className="text-sm text-gray-600 font-semibold">
                                        Progress:
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <div className="w-24 bg-gray-200 rounded-full h-2">
                                            <div
                                                className="bg-green-600 h-2 rounded-full transition-all duration-300"
                                                style={{
                                                    width: `${rowCount > 0 ? (acceptedCount / rowCount) * 100 : 0}%`,
                                                }}
                                            />
                                        </div>
                                        <span className="text-sm font-medium text-gray-700">
                                            {rowCount > 0
                                                ? Math.round(
                                                      (acceptedCount / rowCount) * 100,
                                                  )
                                                : 0}
                                            %
                                        </span>
                                        <span className="text-xs text-gray-500">
                                            ({acceptedCount}/{rowCount})
                                        </span>
                                    </div>
                                </div>
                            </>
                        )}
                    </>
                )}
                <div className="ml-auto flex gap-2">
                    <ExportXLSX
                        selectedAudit={selectedAudit.value}
                        filename={selectedAudit.value?.title || ''}
                    ></ExportXLSX>
                    <ImportCSV selectedAudit={selectedAudit.value}></ImportCSV>
                    <button
                        onClick={() => setIsSidebarOpen(true)}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        <Bars3Icon className="h-5 w-5" aria-hidden="true" />
                    </button>
                </div>
            </div>
            {/* Tab Navigation */}
            <div className="border-b border-gray-200">
                <nav className="-mb-px flex px-4" aria-label="Tabs">
                    <button
                        onClick={() => setActiveTab('grid')}
                        className={`${
                            activeTab === 'grid'
                                ? 'border-indigo-500 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                        } whitespace-nowrap border-b-2 py-2 px-4 text-sm font-medium`}
                    >
                        Audit Grid
                    </button>
                    <button
                        onClick={() => setActiveTab('properties')}
                        className={`${
                            activeTab === 'properties'
                                ? 'border-indigo-500 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                        } whitespace-nowrap border-b-2 py-2 px-4 text-sm font-medium`}
                    >
                        Worksheet Properties
                    </button>
                </nav>
            </div>
        </div>
    );

    const bodyContent = (
        <>
            {activeTab === 'grid' ? (
                <>
                    <AuditGrid
                        ref={auditGridRef}
                        audit={selectedAudit.value}
                        onModelUpdated={updateRowCount}
                        onFirstDataRendered={updateRowCount}
                        onCellValueChanged={updateRowCount}
                    ></AuditGrid>
                    <SlidingSidebar
                        isOpen={isSidebarOpen}
                        onClose={() => setIsSidebarOpen(false)}
                        title="Data Import"
                        width="xl"
                    >
                        <PasteGrid onDataChange={handlePastedDataChange} />
                    </SlidingSidebar>
                </>
            ) : (
                <WorksheetProperties selectedAudit={selectedAudit.value} />
            )}
        </>
    );

    return <MicroWrapper header={headerContent} body={bodyContent} />;
}

export default Worksheet;
